import React, { useContext, useState } from "react";
import { useTableData } from "../../../hooks/useTableData";
import { deleteTeamMember, getTeamMembers } from "../../../apis/workspace/teams";
import { useMutation, useQueryClient } from "react-query";
import SharedDataContext from "../../../SharedDataContext";
import { useParams } from "react-router-dom";
import { isEmptyObject, returnArray } from "../../../utils/GenericFn";
import Filter from "../../Filter";
import EditColumn from "../../EditColumn";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Button } from "../../ui/button";
import Image from "../../Tags/Image";
import { Tooltip } from "react-tooltip";
import TableLoader from "../../Loaders/TableLoader";
import NoData from "../../NoData";
import Pagination from "../../Pagination";
import PrintDate from "../../Tags/PrintDate";
import ConfirmModal from "../../Modals/ConfirmModal";
import toast from "react-hot-toast";

export default function TeamMembers() {
  const queryClient = useQueryClient();
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const { teamId } = useParams();

  // get team members
  const [members, setMembers] = useState({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const {
    data,
    paginationInfo,
    filterMetadata,
    columnMetadata,
    apiResponse,
    isLoading,
    isFetching,
    isFetched,
    isSuccess,
    isError,
    filters,
    sort,
    pagination,
    columns,
    sortedVisibleColumns,
    getPageNumber,
    getFiltersObject,
    handleSetSort,
    getColumns,
  } = useTableData({
    queryKey: "get_members_of_team",
    fetchFn: getTeamMembers,
    defaultHeaders: { "X-Workspace-Id": activeWorkspace?.id },
    restObject: { teamId: teamId },

    enabled: !!activeWorkspace?.id,
    onSuccess: (data) => {
      //   const mappedData = mapApiResponseToTableData(data);
      console.log("data", data);
      setMembers(data);
      setIsInitialLoad(false);

      console.log("Fetched!", data);
    },
    onError: (error) => {
      console.error("CallLogs API error:", error);
      if (error?.status === 403 || error?.response?.status === 403) {
        setIsInitialLoad(false);
      } else {
      }
    },
  });

  const renderCellContent = (columnKey, item) => {
    switch (columnKey) {
      case "user_name":
        return <>{item?.name}</>;
      case "user_email":
        return <>{item?.email}</>;
      case "added_by":
        return (
          <div className="flex items-center">
            <div className="h-9 w-9 flex-shrink-0">
              <Image
                nobaseurl
                className="h-9 w-9 rounded-full"
                src={item?.added_by?.profile_photo_url}
                alt=""
              />
            </div>
            <div className="ml-4">
              <div className="font-medium ">{item?.added_by?.name}</div>
              <div className="mt-0 text-gray-500">{item?.added_by?.email}</div>
            </div>
          </div>
        );

      case "created_at":
        return <PrintDate date={item?.created_at} showTime={true} />;
      case "updated_at":
        return <PrintDate date={item?.updated_at} showTime={true} />;
      default:
        return item?.[columnKey] || "-";
    }
  };

  // handle delete team
  const [currentMemberId, setCurrentMemberId] = useState("");
  const deleteTeamMemberFn = useMutation("delete_team_member", deleteTeamMember, {
    onSuccess(data, variables, context) {
      toast.success(data?.message);
      queryClient.invalidateQueries("get_members_of_team");
      queryClient.invalidateQueries("view_team");
    },
  });
  const handleDeleteTeamMember = () => {
    deleteTeamMemberFn.mutate({
      teamId: teamId,
      memberId: currentMemberId,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  // handle open confirm
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openConfirmModalType, setOpenConfirmModalType] = useState("");
  const confirmModalState = (data) => {
    setOpenConfirmModal(data.false);
    //console.log(data);
    if (data && data.confirm && data.type && data.type === "delete") {
      handleDeleteTeamMember();
    }
  };

  return (
    <div>
      {members?.data && (members.data.length > 0 || !isEmptyObject(filters)) && (
        <div className="flex justify-between items-center mt-4">
          <div className="space-x-3 inline-flex items-center">
            {members.filterMetadata && (
              <>
                <span className="text-sm text-gray-500">Filters:</span>
                <Filter filterMetadata={members.filterMetadata} getFiltersObject={getFiltersObject} />
              </>
            )}
          </div>
          <div className="space-x-3">
            {members.columnMetadata && (
              <EditColumn columnMetadata={members.columnMetadata} getColumns={getColumns} />
            )}
          </div>
        </div>
      )}
      {members?.data && members.data.length > 0 && isSuccess && (
        <div className="mt-6 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden border border-border rounded-[--radius]">
                <table className="min-w-full divide-y divide-border table-fixed">
                  <thead className="bg-gray-100">
                    <tr>
                      {returnArray(sortedVisibleColumns).map((column, index) => (
                        <th
                          key={index}
                          scope="col"
                          className={`px-3 py-3.5 text-left text-xs font-medium text-text-600 ${
                            column.key === "user_name" ? "w-1/4" : ""
                          } ${column.sortable ? "cursor-pointer" : ""}`}
                          onClick={() => (column.sortable ? handleSetSort(column.key) : void 0)}
                        >
                          <span className={"inline-flex items-center"}>
                            {column.display_name}
                            {column.sortable && (
                              <>
                                {sort === `-${column.key}` ? (
                                  <ArrowDown className="inline-block ml-1 h-3 w-3" />
                                ) : sort === column.key ? (
                                  <ArrowUp className="inline-block ml-1 h-3 w-3" />
                                ) : (
                                  <ArrowDown className="inline-block ml-1 h-3 w-3 opacity-50" />
                                )}
                              </>
                            )}
                          </span>
                        </th>
                      ))}
                      <th scope="col" className="!px-3 !py-4 text-left text-sm font-medium" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    {members.data &&
                      members.data.length > 0 &&
                      returnArray(members.data).map((item, index) => (
                        <tr key={item.id} className={`hover:bg-gray-50  ${index % 2 !== 0 ? "" : ""}`}>
                          {returnArray(sortedVisibleColumns).map(({ key: colKey }) => (
                            <td
                              key={colKey}
                              className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-[#F7F7F7] "
                            >
                              {renderCellContent(colKey, item)}
                            </td>
                          ))}
                          <td className="whitespace-nowrap  px-3 py-4">
                            <div className="flex justify-end w-full">
                              <Button
                                data-tooltip-id="in_table_actions"
                                data-tooltip-content="Remove member from this Team"
                                variant="ghost"
                                onClick={() => {
                                  setCurrentMemberId(item?.id);
                                  setOpenConfirmModal(true);
                                  setOpenConfirmModalType("delete");
                                }}
                              >
                                <Image
                                  src="/images/icons/untitledui/trash.svg"
                                  width="16"
                                  height="16"
                                  className="inline-block"
                                />
                              </Button>
                              <Tooltip id="in_table_actions" />
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      {(isLoading || isInitialLoad) && <TableLoader />}

      {members?.data?.length === 0 && !isLoading && !isError && (
        <NoData
          className="text-center inline-block"
          icon={
            <>
              <Image src="/images/icons/no-members.svg" className="inline-block" />
            </>
          }
          heading="It looks like this team has no members yet."
          description=""
        />
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between rounded-[--radius] py-3">
        <div className="flex-1">
          {members.pagination && members.pagination.total > 0 && (
            <Pagination data={members.pagination} getPageNumber={getPageNumber} />
          )}
        </div>
      </div>

      <ConfirmModal
        openModal={openConfirmModal}
        modalType={openConfirmModalType}
        confirmModalState={confirmModalState}
      />
    </div>
  );
}
